"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import ExperienceCard from "@/components/getting-started/ExperienceCard";

import { PageTwitterSchema } from "@/sanity/pages/PageTwitterTemplate";

import blurImg from "@/public/lp2/experience/bottom-blur.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function ComponentsSection({
  data,
  bitunix = false,
  phemex = false,
}: {
  data: PageTwitterSchema;
  bitunix?: boolean;
  phemex?: boolean;
}) {
  const containerRef2 = useRef<HTMLDivElement | any>(undefined);
  const content2Ref = useRef<HTMLDivElement | any>(undefined);
  const content3Ref = useRef<HTMLDivElement | any>(undefined);
  const content4Ref = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const experienceTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      if (content2Ref.current) {
        experienceTL.from(content2Ref.current.children, {
          y: "2rem",
          stagger: 0.1,
          delay: 0.1,
          scrollTrigger: {
            trigger: content2Ref.current,
            end: "center center",
            scrub: 1,
          },
        });
      }

      if (content3Ref.current) {
        experienceTL.from(content3Ref.current, {
          y: "2rem",
          delay: 0.1,
          clearProps: "all",
          scrollTrigger: {
            trigger: content3Ref.current,
            end: "center center",
            scrub: 1,
          },
        });
      }
    },
    { scope: containerRef2 }
  );

  return (
    <section
      ref={containerRef2}
      className="w-full relative bg-black mix-blend-exclusion overflow-x-clip"
    >
      <div className="container-lp mx-auto pt-[16rem] lg:pt-[24rem] lg:pb-[10rem]">
        <div
          ref={content2Ref}
          className="w-full md:w-4/5 lg:w-[65%] flex flex-col items-center justify-center mx-auto mb-[3rem] lg:mb-[4rem] relative"
        >
          {/* shadow/blur */}
          <Image
            src={blurImg}
            alt="blur background"
            className="w-[300%] max-w-max lg:w-full absolute left-[50%] top-[-50%] lg:top-[-100%] translate-x-[-50%] translate-y-[-50%] z-[-1] pointer-events-none"
          />
          {/*  */}
          {data?.componentsTexts && (
            <PortableText
              value={data?.componentsTexts ?? []}
              components={customRichTextComponent({
                block: {
                  h2: ({ children }) => (
                    <h2 className="gradient-title text-[5rem] lg:text-[7rem] font-[600] tracking-[-0.35rem] lg:tracking-[-0.49rem] leading-[110%] lg:leading-[8.875rem] text-center mb-[2.4rem] w-[90%] md:w-[60%] lg:w-full lg:px-[10rem]">
                      {children}
                    </h2>
                  ),
                },
                marks: {
                  orangeFlare: ({ children }) => (
                    <span
                      className={
                        bitunix
                          ? "bitunix-shadow"
                          : phemex
                            ? "phemex-shadow"
                            : "orange-shadow"
                      }
                    >
                      {children}
                    </span>
                  ),
                },
              })}
            />
          )}
        </div>

        <div
          ref={content3Ref}
          className="w-full flex flex-col lg:flex-row justify-center items-center gap-[2rem] lg:gap-[3rem] mb-[2rem] lg:mb-[3rem] relative z-[1]"
        >
          <ExperienceCard
            cardNumber={1}
            cardTitle={data?.components?.[0]?.title}
            cardText={data?.components?.[0]?.text}
            cardImg={data?.components?.[0]?.image}
            cardImgMobile={data?.components?.[0]?.mobileImage}
          />
          <ExperienceCard
            cardNumber={2}
            cardTitle={data?.components?.[1]?.title}
            cardText={data?.components?.[1]?.text}
            cardImg={data?.components?.[1]?.image}
            cardImgMobile={data?.components?.[1]?.mobileImage}
          />
        </div>
        {/*  */}
        <div
          ref={content4Ref}
          className="w-full flex flex-col lg:flex-row justify-center items-center gap-[2rem] lg:gap-[3rem]"
        >
          <ExperienceCard
            cardNumber={3}
            cardTitle={data?.components?.[2]?.title}
            cardText={data?.components?.[2]?.text}
            cardImg={data?.components?.[2]?.image}
            cardImgMobile={data?.components?.[2]?.mobileImage}
          />
          <ExperienceCard
            cardNumber={4}
            cardTitle={data?.components?.[3]?.title}
            cardText={data?.components?.[3]?.text}
            cardImg={data?.components?.[3]?.image}
            cardImgMobile={data?.components?.[3]?.mobileImage}
          />
        </div>
      </div>
    </section>
  );
}
